import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { EquipmentDetail as EquipmentDetailType, ChangeoutItem, WorkOrderItem, C1InspectionItem } from '../types';

interface DocumentItem {
  id: string;
  document_type: string;
  title: string;
  filename: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  uploaded_at: string | null;
  ai_processed: boolean;
  component_id: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  in_service: '#4caf50', in_stores: '#ff9800', under_repair: '#f44336',
  under_overhaul: '#9c27b0', decommissioned: '#757575', condemned: '#d32f2f',
  spare: '#2196f3', removed: '#9e9e9e',
};

const CHANGEOUT_STATUS: Record<string, { label: string; color: string }> = {
  pending_eem: { label: 'Pending EEM Approval', color: '#ff9800' },
  eem_approved: { label: 'EEM Approved — PSF Update Required', color: '#2196f3' },
  psf_updated: { label: 'PSF Updated', color: '#8bc34a' },
  complete: { label: 'Complete', color: '#4caf50' },
};

const REASON_LABELS: Record<string, string> = {
  overhaul_due: 'Overhaul Due', fault: 'Fault', damage: 'Damage',
  upgrade: 'Upgrade', mda_replacement: 'MDA Replacement', other: 'Other',
};

// Updated doc type labels — includes all PSF document types
const DOC_TYPE_LABELS: Record<string, string> = {
  certificate_of_conformity: 'Certificate of Conformity',
  overhaul_certificate: 'Overhaul Certificate',
  test_certificate: 'Test Certificate',
  conditions_of_use: 'Conditions of Use',
  as5368_audit: 'AS/NZS 5368 Audit',
  as4871_audit: 'AS/NZS 4871 Audit',
  iec60079_25_audit: 'IEC 60079.25 IS Audit',
  cable_schedule: 'Cable Schedule',
  circuit_drawings: 'Circuit Drawings',
  risk_assessment: 'Risk Assessment',
  general: 'General',
  // Legacy types
  psf_cover: 'PSF Cover',
  ex_schedule: 'Ex Schedule',
  inspection_report: 'Inspection Report',
  compliance_audit: 'Compliance Audit',
  drawing: 'Drawing',
  schematic: 'Schematic',
  wiring_diagram: 'Wiring Diagram',
  oem_manual: 'OEM Manual',
  function_test: 'Function Test',
  flamepath_inspection: 'Flamepath Inspection',
  nameplate_photo: 'Nameplate Photo',
  component_photo: 'Component Photo',
  its_form: 'ITS Form',
  its_checklist: 'ITS Checklist',
  other: 'Other',
};

const DOC_TYPE_ICONS: Record<string, string> = {
  certificate_of_conformity: '📜',
  overhaul_certificate: '🔧',
  test_certificate: '🧪',
  conditions_of_use: '📋',
  as5368_audit: '📋',
  as4871_audit: '📋',
  iec60079_25_audit: '⚡',
  cable_schedule: '🔌',
  circuit_drawings: '📐',
  risk_assessment: '🎯',
  general: '📄',
  psf_cover: '📕',
  ex_schedule: '⚡',
  inspection_report: '🔍',
  compliance_audit: '📋',
  drawing: '📐',
  schematic: '📐',
  wiring_diagram: '🔌',
  oem_manual: '📖',
  function_test: '🧪',
  flamepath_inspection: '🔍',
  nameplate_photo: '🏷️',
  component_photo: '📷',
  its_form: '📝',
  its_checklist: '✅',
  other: '📎',
};

// PSF document type order (preferred display order)
const PSF_DOC_ORDER = [
  'certificate_of_conformity',
  'overhaul_certificate',
  'test_certificate',
  'conditions_of_use',
  'as5368_audit',
  'as4871_audit',
  'iec60079_25_audit',
  'cable_schedule',
  'circuit_drawings',
  'risk_assessment',
  'general',
];

const WO_TYPE_COLORS: Record<string, string> = {
  preventive: '#2196f3',
  corrective: '#f44336',
  condition_based: '#ff9800',
  inspection: '#9c27b0',
  c1_inspection: '#4caf50',
};

const WO_STATUS_COLORS: Record<string, string> = {
  open: '#ff9800',
  in_progress: '#2196f3',
  completed: '#4caf50',
  cancelled: '#757575',
};

const EquipmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [eq, setEq] = useState<EquipmentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [changeouts, setChangeouts] = useState<ChangeoutItem[]>([]);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('certificate_of_conformity');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadComponentId, setUploadComponentId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Maintenance state
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [c1Inspections, setC1Inspections] = useState<C1InspectionItem[]>([]);
  const [showWOForm, setShowWOForm] = useState(false);
  const [showC1Form, setShowC1Form] = useState(false);
  const [woExpanded, setWoExpanded] = useState<string | null>(null);
  const [c1Expanded, setC1Expanded] = useState<string | null>(null);

  // WO form state
  const [woNumber, setWoNumber] = useState('');
  const [woType, setWoType] = useState('preventive');
  const [woSource, setWoSource] = useState('');
  const [woTitle, setWoTitle] = useState('');
  const [woDesc, setWoDesc] = useState('');
  const [woPerformedBy, setWoPerformedBy] = useState('');
  const [woTrade, setWoTrade] = useState('electrical');
  const [woDate, setWoDate] = useState(new Date().toISOString().split('T')[0]);
  const [woCompletedDate, setWoCompletedDate] = useState('');
  const [woStatus, setWoStatus] = useState('completed');
  const [woComponentId, setWoComponentId] = useState('');
  const [woFindings, setWoFindings] = useState('');
  const [woActions, setWoActions] = useState('');
  const [woSubmitting, setWoSubmitting] = useState(false);

  // C1 form state
  const [c1Inspector, setC1Inspector] = useState('');
  const [c1Licence, setC1Licence] = useState('');
  const [c1Org, setC1Org] = useState('');
  const [c1Date, setC1Date] = useState(new Date().toISOString().split('T')[0]);
  const [c1NextDue, setC1NextDue] = useState('');
  const [c1Result, setC1Result] = useState('pass');
  const [c1Notes, setC1Notes] = useState('');
  const [c1ExtMonths, setC1ExtMonths] = useState<number | ''>('');
  const [c1OrigDue, setC1OrigDue] = useState('');
  const [c1NewDue, setC1NewDue] = useState('');
  const [c1Findings, setC1Findings] = useState('');
  const [c1Recs, setC1Recs] = useState('');
  const [c1ComponentId, setC1ComponentId] = useState('');
  const [c1WONum, setC1WONum] = useState('');
  const [c1Number, setC1Number] = useState('');
  const [c1Submitting, setC1Submitting] = useState(false);

  // Change-out state
  const [showChangeout, setShowChangeout] = useState(false);
  const [coComponentId, setCoComponentId] = useState('');
  const [coDate, setCoDate] = useState(new Date().toISOString().split('T')[0]);
  const [coReason, setCoReason] = useState('overhaul_due');
  const [coReasonDetail, setCoReasonDetail] = useState('');
  const [coNewSerial, setCoNewSerial] = useState('');
  const [coNewCert, setCoNewCert] = useState('');
  const [coNewMfr, setCoNewMfr] = useState('');
  const [coNewModel, setCoNewModel] = useState('');
  const [coPerformedBy, setCoPerformedBy] = useState('');
  const [coWorkOrder, setCoWorkOrder] = useState('');
  const [coNotes, setCoNotes] = useState('');
  const [coSubmitting, setCoSubmitting] = useState(false);

  const loadEquipment = useCallback(async () => {
    try {
      const { data } = await api.get<EquipmentDetailType>(`/api/equipment/${id}`);
      setEq(data);
      return data;
    } catch (err) { console.error(err); return null; }
  }, [id]);

  const loadDocuments = useCallback(async (orgId: string) => {
    try {
      const { data } = await api.get('/api/documents/', { params: { org_id: orgId, equipment_id: id } });
      setDocuments(data.items);
    } catch (err) { console.error(err); }
  }, [id]);

  const loadChangeouts = useCallback(async () => {
    try {
      const { data } = await api.get<ChangeoutItem[]>('/api/changeouts/', { params: { equipment_id: id } });
      setChangeouts(data);
    } catch (err) { console.error(err); }
  }, [id]);

  const loadWorkOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/maintenance/work-orders', { params: { equipment_id: id } });
      setWorkOrders(data.items);
    } catch (err) { console.error(err); }
  }, [id]);

  const loadC1Inspections = useCallback(async () => {
    try {
      const { data } = await api.get('/api/maintenance/c1-inspections', { params: { equipment_id: id } });
      setC1Inspections(data.items);
    } catch (err) { console.error(err); }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      const data = await loadEquipment();
      if (data) { await loadDocuments(data.organisation_id); }
      await loadChangeouts();
      await loadWorkOrders();
      await loadC1Inspections();
      setLoading(false);
    };
    init();
  }, [loadEquipment, loadDocuments, loadChangeouts, loadWorkOrders, loadC1Inspections]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !eq) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('organisation_id', eq.organisation_id);
      fd.append('equipment_id', eq.id);
      fd.append('document_type', uploadType);
      fd.append('title', uploadTitle || uploadFile.name);
      if (uploadComponentId) fd.append('component_id', uploadComponentId);
      await api.post('/api/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadFile(null); setUploadTitle(''); setUploadComponentId(''); setShowUpload(false);
      await loadDocuments(eq.organisation_id);
    } catch (err: any) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleChangeout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eq || !coComponentId) return;
    setCoSubmitting(true);
    const comp = eq.components.find(c => c.id === coComponentId);
    try {
      await api.post('/api/changeouts/', {
        equipment_id: eq.id,
        old_component_id: coComponentId,
        schedule_number: comp?.schedule_number,
        new_serial_number: coNewSerial || null,
        new_certificate_number: coNewCert || null,
        new_manufacturer: coNewMfr || null,
        new_model: coNewModel || null,
        changeout_date: coDate,
        reason: coReason,
        reason_detail: coReasonDetail || null,
        work_order_number: coWorkOrder || null,
        performed_by: coPerformedBy || null,
        notes: coNotes || null,
      });
      setShowChangeout(false);
      setCoComponentId(''); setCoNewSerial(''); setCoNewCert(''); setCoNewMfr(''); setCoNewModel('');
      setCoReasonDetail(''); setCoPerformedBy(''); setCoWorkOrder(''); setCoNotes('');
      const data = await loadEquipment();
      if (data) await loadDocuments(data.organisation_id);
      await loadChangeouts();
    } catch (err: any) { alert(err.response?.data?.detail || 'Failed to record change-out'); }
    finally { setCoSubmitting(false); }
  };

  const handleEEMApprove = async (coId: string) => {
    const name = prompt('EEM name for approval:');
    if (!name) return;
    try {
      await api.post(`/api/changeouts/${coId}/eem-approve`, { eem_name: name });
      await loadChangeouts();
    } catch (err: any) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handlePSFUpdate = async (coId: string) => {
    try {
      await api.post(`/api/changeouts/${coId}/psf-update`);
      await loadChangeouts();
    } catch (err: any) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleWOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eq) return;
    setWoSubmitting(true);
    try {
      await api.post('/api/maintenance/work-orders', {
        organisation_id: eq.organisation_id,
        equipment_id: eq.id,
        component_id: woComponentId || null,
        work_order_number: woNumber,
        work_order_type: woType,
        source_system: woSource || null,
        title: woTitle,
        description: woDesc || null,
        performed_by: woPerformedBy || null,
        trade_type: woTrade || null,
        performed_date: woDate,
        completed_date: woCompletedDate || null,
        status: woStatus,
        findings: woFindings || null,
        actions_taken: woActions || null,
      });
      setShowWOForm(false);
      setWoNumber(''); setWoTitle(''); setWoDesc(''); setWoPerformedBy('');
      setWoFindings(''); setWoActions(''); setWoComponentId(''); setWoSource('');
      await loadWorkOrders();
    } catch (err: any) { alert(err.response?.data?.detail || 'Failed to create work order'); }
    finally { setWoSubmitting(false); }
  };

  const handleC1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eq) return;
    setC1Submitting(true);
    try {
      await api.post('/api/maintenance/c1-inspections', {
        organisation_id: eq.organisation_id,
        equipment_id: eq.id,
        component_id: c1ComponentId || null,
        inspection_number: c1Number || null,
        inspector_name: c1Inspector,
        inspector_licence: c1Licence || null,
        inspector_organisation: c1Org || null,
        inspection_date: c1Date,
        next_inspection_due: c1NextDue || null,
        inspection_result: c1Result,
        condition_notes: c1Notes || null,
        overhaul_extension_months: c1ExtMonths || null,
        original_overhaul_due: c1OrigDue || null,
        new_overhaul_due: c1NewDue || null,
        findings: c1Findings || null,
        recommendations: c1Recs || null,
        work_order_number: c1WONum || null,
      });
      setShowC1Form(false);
      setC1Inspector(''); setC1Licence(''); setC1Org(''); setC1Notes('');
      setC1Findings(''); setC1Recs(''); setC1ComponentId(''); setC1Number('');
      setC1NextDue(''); setC1OrigDue(''); setC1NewDue(''); setC1WONum('');
      setC1ExtMonths('');
      await loadC1Inspections();
      await loadEquipment(); // Refresh overhaul dates if extended
    } catch (err: any) { alert(err.response?.data?.detail || 'Failed to record C1 inspection'); }
    finally { setC1Submitting(false); }
  };

  const fmt = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const isOD = (d: string | null) => d ? new Date(d) < new Date() : false;
  const fmtB = (b: number | null) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  // Build map: component_id → CoC documents
  const componentDocMap: Record<string, DocumentItem[]> = {};
  documents.forEach(doc => {
    if (doc.component_id) {
      if (!componentDocMap[doc.component_id]) componentDocMap[doc.component_id] = [];
      componentDocMap[doc.component_id].push(doc);
    }
  });

  // Find the CoC document for a component
  const getCoCDoc = (componentId: string): DocumentItem | null => {
    const compDocs = componentDocMap[componentId] || [];
    return compDocs.find(d => d.document_type === 'certificate_of_conformity') || compDocs[0] || null;
  };

  // Get download URL for a document (includes JWT token for new-tab access)
  const getDocUrl = (docId: string) => {
    const token = localStorage.getItem('psf_token');
    return `/api/documents/${docId}/download?token=${encodeURIComponent(token || '')}`;
  };

  // Group documents by type for PSF Documents section
  const docsByType: Record<string, DocumentItem[]> = {};
  documents.forEach(doc => {
    const type = doc.document_type || 'other';
    if (!docsByType[type]) docsByType[type] = [];
    docsByType[type].push(doc);
  });

  // Sort grouped types: PSF_DOC_ORDER first, then alphabetically
  const sortedDocTypes = Object.keys(docsByType).sort((a, b) => {
    const ai = PSF_DOC_ORDER.indexOf(a);
    const bi = PSF_DOC_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  if (loading) return <div style={s.loadingFull}>Loading...</div>;
  if (!eq) return <div style={s.loadingFull}>Equipment not found</div>;

  const exC = eq.components.filter(c => c.explosion_protected);
  const nonExC = eq.components.filter(c => !c.explosion_protected);
  const pendingCO = changeouts.filter(c => c.status !== 'complete');

  return (
    <div style={s.container}>
      {/* Header */}
      <header style={s.header}>
        <div style={s.headerLeft}>
          <button onClick={() => navigate('/')} style={s.backBtn}>← Dashboard</button>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '36px' }} />
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={s.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
        </div>
      </header>

      <div style={s.content}>
        {/* Equipment Card */}
        <div style={s.card}>
          <div style={s.eqHeader}>
            <div>
              <h2 style={s.eqTag}>{eq.equipment_tag}</h2>
              <p style={s.eqDesc}>{eq.description}</p>
            </div>
            <div style={s.badges}>
              <button
                onClick={() => navigate(`/chat?equipment_id=${eq.id}&org_id=${eq.organisation_id}`)}
                style={{ ...s.badge, background: '#0f3460', cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
              >
                🤖 Ask AI
              </button>
              <span style={{ ...s.badge, background: STATUS_COLORS[eq.status] || '#999' }}>{eq.status.replace(/_/g, ' ')}</span>
              {eq.explosion_protected && <span style={s.exBadge}>⚡ Ex Protected</span>}
              {eq.mda_count > 0 && <span style={s.mdaBadge}>⚠️ {eq.mda_count} MDA</span>}
              {pendingCO.length > 0 && <span style={s.coBadge}>🔄 {pendingCO.length} Pending Change-out{pendingCO.length > 1 ? 's' : ''}</span>}
            </div>
          </div>
          <div style={s.grid4}>
            <Info label="Equipment Type" value={eq.equipment_type.replace(/_/g, ' ')} />
            <Info label="Manufacturer" value={eq.manufacturer} />
            <Info label="Model" value={eq.model} />
            <Info label="Serial Number" value={eq.serial_number} />
            <Info label="Owner" value={eq.owner} />
            <Info label="In Service Date" value={fmt(eq.in_service_date)} />
            <Info label="Last Overhaul" value={fmt(eq.last_overhaul_date)} />
            <Info label="Next Overhaul Due" value={fmt(eq.next_overhaul_due)} alert={isOD(eq.next_overhaul_due)} />
            <Info label="Overhaul Workshop" value={eq.overhaul_workshop} />
            <Info label="Job Number" value={eq.overhaul_job_number} />
            <Info label="PSF Status" value={eq.psf_status?.replace(/_/g, ' ')} />
            <Info label="Components" value={`${eq.component_count} total (${eq.ex_component_count} Ex)`} />
          </div>
          {eq.notes && <div style={s.notes}><strong>Notes:</strong> {eq.notes}</div>}
        </div>

        {/* Ex Component Register */}
        {exC.length > 0 && (
          <Section title={`⚡ Ex Equipment Register (${exC.length} items)`}>
            <div style={s.tWrap}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>#</th><th style={s.th}>Description</th><th style={s.th}>Manufacturer</th>
                  <th style={s.th}>Serial No</th><th style={s.th}>Protection</th><th style={s.th}>Certificate</th>
                  <th style={s.th}>Gas Grp</th><th style={s.th}>Last Overhaul</th><th style={s.th}>Next Due</th>
                  <th style={s.th}>In Service</th><th style={s.th}>Status</th><th style={s.th}>Flags</th>
                </tr></thead>
                <tbody>
                  {exC.map(c => {
                    const cocDoc = getCoCDoc(c.id);
                    return (
                      <tr key={c.id} style={s.tr}>
                        <td style={s.td}>{c.schedule_number || '—'}</td>
                        <td style={{ ...s.td, fontWeight: 500 }}>{c.description || '—'}</td>
                        <td style={s.td}>{c.manufacturer || '—'}</td>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{c.serial_number || '—'}</td>
                        <td style={s.td}><span style={s.protBadge}>{c.protection_type}</span></td>
                        <td style={{ ...s.td, fontSize: '11px' }}>
                          {c.certificate_number ? (
                            cocDoc ? (
                              <a
                                href={getDocUrl(cocDoc.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={s.certLink}
                                title={`View ${cocDoc.title}`}
                                onClick={e => e.stopPropagation()}
                              >
                                📜 {c.certificate_number}
                              </a>
                            ) : (
                              <span style={s.certNoDoc} title="No document linked — upload a Certificate of Conformity">
                                {c.certificate_number}
                                <span style={s.certNoDocBadge}>no doc</span>
                              </span>
                            )
                          ) : '—'}
                        </td>
                        <td style={s.td}>{c.gas_group || '—'}</td>
                        <td style={s.td}>{fmt(c.last_overhaul_date)}</td>
                        <td style={s.td}>
                          <span style={{ color: isOD(c.next_overhaul_due) ? '#d32f2f' : '#333', fontWeight: isOD(c.next_overhaul_due) ? 600 : 400 }}>
                            {fmt(c.next_overhaul_due)}
                          </span>
                        </td>
                        <td style={s.td}>{fmt(c.in_service_date)}</td>
                        <td style={s.td}>
                          <span style={{ ...s.sBadge, background: STATUS_COLORS[c.status] || '#999' }}>{c.status.replace(/_/g, ' ')}</span>
                        </td>
                        <td style={s.td}>
                          {c.is_mda && <span style={s.mdaFlag}>⚠️ MDA</span>}
                          {c.label_correct === false && <span style={s.alertFlag}>Label ✗</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Non-Ex Components */}
        {nonExC.length > 0 && (
          <Section title={`Other Components (${nonExC.length})`}>
            <div style={s.tWrap}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>#</th><th style={s.th}>Description</th><th style={s.th}>Type</th>
                  <th style={s.th}>Manufacturer</th><th style={s.th}>Serial No</th>
                  <th style={s.th}>Last Overhaul</th><th style={s.th}>In Service</th><th style={s.th}>Status</th>
                </tr></thead>
                <tbody>
                  {nonExC.map(c => (
                    <tr key={c.id} style={s.tr}>
                      <td style={s.td}>{c.schedule_number || '—'}</td>
                      <td style={s.td}>{c.description || '—'}</td>
                      <td style={s.td}>{c.component_type.replace(/_/g, ' ')}</td>
                      <td style={s.td}>{c.manufacturer || '—'}</td>
                      <td style={s.td}>{c.serial_number || '—'}</td>
                      <td style={s.td}>{fmt(c.last_overhaul_date)}</td>
                      <td style={s.td}>{fmt(c.in_service_date)}</td>
                      <td style={s.td}><span style={{ ...s.sBadge, background: STATUS_COLORS[c.status] || '#999' }}>{c.status.replace(/_/g, ' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* PSF Documents Section (grouped by type) */}
        <Section
          title={`📁 PSF Documents (${documents.length})`}
          action={<button onClick={() => setShowUpload(!showUpload)} style={s.actionBtn}>{showUpload ? 'Cancel' : '+ Upload Document'}</button>}
        >
          {showUpload && (
            <form onSubmit={handleUpload} style={s.formCard}>
              {/* Drag & Drop Zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault(); setDragOver(false);
                  const f = e.dataTransfer.files[0];
                  if (f) { setUploadFile(f); if (!uploadTitle) setUploadTitle(f.name.replace(/\.[^/.]+$/, '')); }
                }}
                onClick={() => document.getElementById('file-input')?.click()}
                style={{
                  ...s.dropZone,
                  borderColor: dragOver ? '#116dff' : uploadFile ? '#4caf50' : '#ddd',
                  background: dragOver ? '#f0f7ff' : uploadFile ? '#f0faf0' : '#fafafa',
                }}
              >
                {uploadFile ? (
                  <div style={{ textAlign: 'center' as const }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      {uploadFile.type.includes('pdf') ? '📄' : uploadFile.type.includes('image') ? '🖼️' : '📎'}
                    </div>
                    <div style={{ fontWeight: 600, color: '#333' }}>{uploadFile.name}</div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                      {(uploadFile.size / 1024).toFixed(0)} KB — Click or drop to replace
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' as const }}>
                    <div style={{ fontSize: '36px', marginBottom: '8px', opacity: 0.5 }}>📁</div>
                    <div style={{ fontWeight: 600, color: '#555' }}>Drop file here or click to browse</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>PDF, images, Word, Excel — max 50MB</div>
                  </div>
                )}
                <input
                  id="file-input"
                  type="file"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setUploadFile(f); if (!uploadTitle) setUploadTitle(f.name.replace(/\.[^/.]+$/, '')); }
                  }}
                  style={{ display: 'none' }}
                />
              </div>

              <div style={{ ...s.grid3, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Document Type</label>
                  <select value={uploadType} onChange={e => setUploadType(e.target.value)} style={s.fInput}>
                    <optgroup label="PSF Documents">
                      <option value="certificate_of_conformity">Certificate of Conformity</option>
                      <option value="overhaul_certificate">Overhaul Certificate</option>
                      <option value="test_certificate">Test Certificate</option>
                      <option value="conditions_of_use">Conditions of Use</option>
                      <option value="as5368_audit">AS/NZS 5368 Audit</option>
                      <option value="iec60079_25_audit">IEC 60079.25 IS Audit</option>
                      <option value="cable_schedule">Cable Schedule</option>
                      <option value="circuit_drawings">Circuit Drawings</option>
                      <option value="risk_assessment">Risk Assessment</option>
                      <option value="general">General</option>
                    </optgroup>
                    <optgroup label="Other">
                      {Object.entries(DOC_TYPE_LABELS)
                        .filter(([k]) => !['certificate_of_conformity','overhaul_certificate','test_certificate','conditions_of_use','as5368_audit','iec60079_25_audit','cable_schedule','circuit_drawings','risk_assessment','general'].includes(k))
                        .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </optgroup>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Title</label>
                  <input type="text" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} style={s.fInput} placeholder="Auto-filled from filename" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Link to Component (optional)</label>
                  <select value={uploadComponentId} onChange={e => setUploadComponentId(e.target.value)} style={s.fInput}>
                    <option value="">Equipment level (general)</option>
                    {eq.components.map(c => <option key={c.id} value={c.id}>#{c.schedule_number} {c.description}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="submit" disabled={!uploadFile || uploading} style={{ ...s.submitBtn, padding: '10px 28px' }}>
                  {uploading ? 'Uploading...' : `Upload ${uploadFile ? uploadFile.name.split('.').pop()?.toUpperCase() : ''}`}
                </button>
              </div>
            </form>
          )}

          {documents.length === 0 && !showUpload ? (
            <div style={s.empty}>No documents uploaded yet. Click "+ Upload Document" to add PSF documents.</div>
          ) : documents.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {sortedDocTypes.map(docType => {
                const typeDocs = docsByType[docType];
                const icon = DOC_TYPE_ICONS[docType] || '📄';
                const label = DOC_TYPE_LABELS[docType] || docType;
                return (
                  <div key={docType}>
                    {/* Type header */}
                    <div style={s.docTypeHeader}>
                      <span style={{ fontSize: '16px' }}>{icon}</span>
                      <span style={s.docTypeLabel}>{label}</span>
                      <span style={s.docTypeCount}>{typeDocs.length}</span>
                    </div>
                    <div style={s.tWrap}>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Title</th>
                            <th style={s.th}>File</th>
                            <th style={s.th}>Size</th>
                            <th style={s.th}>Linked Component</th>
                            <th style={s.th}>Uploaded</th>
                            <th style={s.th}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {typeDocs.map(doc => {
                            const linkedComponent = doc.component_id
                              ? eq.components.find(c => c.id === doc.component_id)
                              : null;
                            return (
                              <tr key={doc.id} style={s.tr}>
                                <td style={{ ...s.td, fontWeight: 500 }}>
                                  {icon} {doc.title}
                                  {doc.ai_processed && (
                                    <span style={s.aiTag}>🤖 AI</span>
                                  )}
                                </td>
                                <td style={{ ...s.td, fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>{doc.filename}</td>
                                <td style={s.td}>{fmtB(doc.file_size_bytes)}</td>
                                <td style={s.td}>
                                  {linkedComponent ? (
                                    <span style={s.componentTag}>
                                      #{linkedComponent.schedule_number} {linkedComponent.description}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '12px', color: '#bbb' }}>Equipment level</span>
                                  )}
                                </td>
                                <td style={s.td}>{fmt(doc.uploaded_at)}</td>
                                <td style={s.td}>
                                  <a
                                    href={getDocUrl(doc.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={s.downloadBtn}
                                  >
                                    {doc.mime_type?.includes('pdf') || doc.filename.endsWith('.pdf') ? '👁 View' : '⬇ Download'}
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </Section>

        {/* Maintenance Work Orders Section */}
        <Section
          title={`🔧 Maintenance Work Orders (${workOrders.length})`}
          action={
            <div style={{ display: 'flex', gap: '8px' }}>
              {workOrders.some(w => w.status === 'open') && (
                <span style={{ ...s.sBadge, background: '#ff9800', fontSize: '11px', padding: '4px 10px' }}>
                  {workOrders.filter(w => w.status === 'open').length} Open
                </span>
              )}
              <button onClick={() => setShowWOForm(!showWOForm)} style={s.actionBtn}>
                {showWOForm ? 'Cancel' : '+ Add Work Order'}
              </button>
            </div>
          }
        >
          {showWOForm && (
            <form onSubmit={handleWOSubmit} style={s.formCard}>
              <h4 style={{ margin: '0 0 16px', fontSize: '14px', color: '#1a1a2e' }}>Record Maintenance Work Order</h4>
              <div style={s.grid4}>
                <div style={s.field}>
                  <label style={s.fLabel}>Work Order # *</label>
                  <input value={woNumber} onChange={e => setWoNumber(e.target.value)} style={s.fInput} required placeholder="WO-2026-XXXX" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Type *</label>
                  <select value={woType} onChange={e => setWoType(e.target.value)} style={s.fInput}>
                    <option value="preventive">Preventive</option>
                    <option value="corrective">Corrective</option>
                    <option value="condition_based">Condition Based</option>
                    <option value="inspection">Inspection</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Source System</label>
                  <select value={woSource} onChange={e => setWoSource(e.target.value)} style={s.fInput}>
                    <option value="">Manual</option>
                    <option value="sap">SAP</option>
                    <option value="pulse">Pulse</option>
                    <option value="pronto">Pronto</option>
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Status *</label>
                  <select value={woStatus} onChange={e => setWoStatus(e.target.value)} style={s.fInput}>
                    <option value="completed">Completed</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div style={{ ...s.grid3, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Title *</label>
                  <input value={woTitle} onChange={e => setWoTitle(e.target.value)} style={s.fInput} required placeholder="Brief description" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Component (optional)</label>
                  <select value={woComponentId} onChange={e => setWoComponentId(e.target.value)} style={s.fInput}>
                    <option value="">Equipment level</option>
                    {eq.components.map(c => <option key={c.id} value={c.id}>#{c.schedule_number} {c.description}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Trade</label>
                  <select value={woTrade} onChange={e => setWoTrade(e.target.value)} style={s.fInput}>
                    <option value="electrical">Electrical</option>
                    <option value="mechanical">Mechanical</option>
                    <option value="instrumentation">Instrumentation</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>
              <div style={{ ...s.grid3, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Performed By</label>
                  <input value={woPerformedBy} onChange={e => setWoPerformedBy(e.target.value)} style={s.fInput} placeholder="Name" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Date Performed *</label>
                  <input type="date" value={woDate} onChange={e => setWoDate(e.target.value)} style={s.fInput} required />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Date Completed</label>
                  <input type="date" value={woCompletedDate} onChange={e => setWoCompletedDate(e.target.value)} style={s.fInput} />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Description</label>
                  <textarea value={woDesc} onChange={e => setWoDesc(e.target.value)} style={{ ...s.fInput, minHeight: '60px', resize: 'vertical' as any }} placeholder="Detailed work description..." />
                </div>
              </div>
              <div style={{ ...s.grid2, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Findings</label>
                  <textarea value={woFindings} onChange={e => setWoFindings(e.target.value)} style={{ ...s.fInput, minHeight: '50px', resize: 'vertical' as any }} placeholder="What was found..." />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Actions Taken</label>
                  <textarea value={woActions} onChange={e => setWoActions(e.target.value)} style={{ ...s.fInput, minHeight: '50px', resize: 'vertical' as any }} placeholder="What was done..." />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="submit" disabled={!woNumber || !woTitle || woSubmitting} style={{ ...s.submitBtn, padding: '10px 28px' }}>
                  {woSubmitting ? 'Saving...' : 'Save Work Order'}
                </button>
              </div>
            </form>
          )}

          {workOrders.length > 0 ? (
            <div style={s.tWrap}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>WO #</th><th style={s.th}>Date</th><th style={s.th}>Type</th>
                  <th style={s.th}>Title</th><th style={s.th}>Component</th><th style={s.th}>Performed By</th>
                  <th style={s.th}>Source</th><th style={s.th}>Status</th>
                </tr></thead>
                <tbody>
                  {workOrders.map(wo => {
                    const comp = wo.component_id ? eq.components.find(c => c.id === wo.component_id) : null;
                    const isExpanded = woExpanded === wo.id;
                    return (
                      <React.Fragment key={wo.id}>
                        <tr style={{ ...s.tr, cursor: 'pointer', background: isExpanded ? '#f8f9ff' : undefined }}
                            onClick={() => setWoExpanded(isExpanded ? null : wo.id)}>
                          <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>{wo.work_order_number}</td>
                          <td style={s.td}>{fmt(wo.performed_date)}</td>
                          <td style={s.td}>
                            <span style={{ ...s.woTypeBadge, background: WO_TYPE_COLORS[wo.work_order_type] || '#999' }}>
                              {wo.work_order_type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ ...s.td, fontWeight: 500, maxWidth: '300px' }}>{wo.title}</td>
                          <td style={s.td}>
                            {comp ? <span style={s.componentTag}>#{comp.schedule_number} {comp.description}</span>
                                   : <span style={{ fontSize: '12px', color: '#bbb' }}>Equipment</span>}
                          </td>
                          <td style={s.td}>{wo.performed_by || '—'}</td>
                          <td style={s.td}>{wo.source_system ? wo.source_system.toUpperCase() : 'Manual'}</td>
                          <td style={s.td}>
                            <span style={{ ...s.sBadge, background: WO_STATUS_COLORS[wo.status] || '#999' }}>
                              {wo.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} style={{ padding: '16px 20px', background: '#f8f9ff', borderBottom: '2px solid #e0e0e0' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                                {wo.description && <div><strong style={{ color: '#666' }}>Description:</strong><br/>{wo.description}</div>}
                                {wo.findings && <div><strong style={{ color: '#666' }}>Findings:</strong><br/>{wo.findings}</div>}
                                {wo.actions_taken && <div><strong style={{ color: '#666' }}>Actions Taken:</strong><br/>{wo.actions_taken}</div>}
                                {wo.completed_date && <div><strong style={{ color: '#666' }}>Completed:</strong> {fmt(wo.completed_date)}</div>}
                                {wo.trade_type && <div><strong style={{ color: '#666' }}>Trade:</strong> {wo.trade_type}</div>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : !showWOForm && (
            <div style={s.empty}>No maintenance work orders recorded. Click "+ Add Work Order" to start building the compliance history.</div>
          )}
        </Section>

        {/* C1 Inspections Section */}
        <Section
          title={`🔍 C1 Pre-Overhaul Inspections (${c1Inspections.length})`}
          action={
            <button onClick={() => setShowC1Form(!showC1Form)} style={s.actionBtn}>
              {showC1Form ? 'Cancel' : '+ Record C1 Inspection'}
            </button>
          }
        >
          {showC1Form && (
            <form onSubmit={handleC1Submit} style={s.formCard}>
              <h4 style={{ margin: '0 0 16px', fontSize: '14px', color: '#1a1a2e' }}>Record C1 Inspection (AS2290.1)</h4>
              <div style={s.grid4}>
                <div style={s.field}>
                  <label style={s.fLabel}>Inspection # </label>
                  <input value={c1Number} onChange={e => setC1Number(e.target.value)} style={s.fInput} placeholder="C1-2026-XXXX" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Inspector Name *</label>
                  <input value={c1Inspector} onChange={e => setC1Inspector(e.target.value)} style={s.fInput} required placeholder="Full name" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Inspector Licence</label>
                  <input value={c1Licence} onChange={e => setC1Licence(e.target.value)} style={s.fInput} placeholder="EWL-XXXXX" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Inspector Organisation</label>
                  <input value={c1Org} onChange={e => setC1Org(e.target.value)} style={s.fInput} placeholder="Company name" />
                </div>
              </div>
              <div style={{ ...s.grid4, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Component</label>
                  <select value={c1ComponentId} onChange={e => setC1ComponentId(e.target.value)} style={s.fInput}>
                    <option value="">Equipment level</option>
                    {eq.components.filter(c => c.explosion_protected).map(c => (
                      <option key={c.id} value={c.id}>#{c.schedule_number} {c.description}</option>
                    ))}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Inspection Date *</label>
                  <input type="date" value={c1Date} onChange={e => setC1Date(e.target.value)} style={s.fInput} required />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Next C1 Due</label>
                  <input type="date" value={c1NextDue} onChange={e => setC1NextDue(e.target.value)} style={s.fInput} />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Result *</label>
                  <select value={c1Result} onChange={e => setC1Result(e.target.value)} style={s.fInput}>
                    <option value="pass">Pass ✅</option>
                    <option value="conditional_pass">Conditional Pass ⚠️</option>
                    <option value="fail">Fail ❌</option>
                  </select>
                </div>
              </div>

              <h4 style={{ margin: '16px 0 12px', fontSize: '13px', color: '#666' }}>Overhaul Extension (if applicable)</h4>
              <div style={s.grid3}>
                <div style={s.field}>
                  <label style={s.fLabel}>Extension (months)</label>
                  <input type="number" value={c1ExtMonths} onChange={e => setC1ExtMonths(e.target.value ? parseInt(e.target.value) : '')} style={s.fInput} placeholder="e.g. 12" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Original Overhaul Due</label>
                  <input type="date" value={c1OrigDue} onChange={e => setC1OrigDue(e.target.value)} style={s.fInput} />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>New Overhaul Due</label>
                  <input type="date" value={c1NewDue} onChange={e => setC1NewDue(e.target.value)} style={s.fInput} />
                </div>
              </div>
              <div style={{ ...s.grid2, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Findings</label>
                  <textarea value={c1Findings} onChange={e => setC1Findings(e.target.value)} style={{ ...s.fInput, minHeight: '60px', resize: 'vertical' as any }} placeholder="Flamepath measurements, IR readings, visual inspection notes..." />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Recommendations</label>
                  <textarea value={c1Recs} onChange={e => setC1Recs(e.target.value)} style={{ ...s.fInput, minHeight: '60px', resize: 'vertical' as any }} placeholder="Follow-up actions, monitoring requirements..." />
                </div>
              </div>
              <div style={{ ...s.grid2, marginTop: '12px' }}>
                <div style={s.field}>
                  <label style={s.fLabel}>Condition Notes</label>
                  <textarea value={c1Notes} onChange={e => setC1Notes(e.target.value)} style={{ ...s.fInput, minHeight: '40px', resize: 'vertical' as any }} placeholder="Overall condition assessment..." />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Related Work Order #</label>
                  <input value={c1WONum} onChange={e => setC1WONum(e.target.value)} style={s.fInput} placeholder="WO-2026-XXXX" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="submit" disabled={!c1Inspector || c1Submitting} style={{ ...s.submitBtn, padding: '10px 28px' }}>
                  {c1Submitting ? 'Recording...' : 'Record C1 Inspection'}
                </button>
              </div>
            </form>
          )}

          {c1Inspections.length > 0 ? (
            <div style={s.tWrap}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Inspection #</th><th style={s.th}>Date</th><th style={s.th}>Inspector</th>
                  <th style={s.th}>Component</th><th style={s.th}>Result</th>
                  <th style={s.th}>Extension</th><th style={s.th}>New Overhaul Due</th>
                </tr></thead>
                <tbody>
                  {c1Inspections.map(c1 => {
                    const comp = c1.component_id ? eq.components.find(c => c.id === c1.component_id) : null;
                    const isExpanded = c1Expanded === c1.id;
                    const resultStyle = c1.inspection_result === 'pass'
                      ? { background: '#4caf50', color: '#fff' }
                      : c1.inspection_result === 'conditional_pass'
                        ? { background: '#ff9800', color: '#fff' }
                        : { background: '#f44336', color: '#fff' };
                    return (
                      <React.Fragment key={c1.id}>
                        <tr style={{ ...s.tr, cursor: 'pointer', background: isExpanded ? '#f8f9ff' : undefined }}
                            onClick={() => setC1Expanded(isExpanded ? null : c1.id)}>
                          <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>{c1.inspection_number || '—'}</td>
                          <td style={s.td}>{fmt(c1.inspection_date)}</td>
                          <td style={s.td}>
                            <div style={{ fontWeight: 500 }}>{c1.inspector_name}</div>
                            {c1.inspector_licence && <div style={{ fontSize: '11px', color: '#888' }}>{c1.inspector_licence}</div>}
                          </td>
                          <td style={s.td}>
                            {comp ? <span style={s.componentTag}>#{comp.schedule_number} {comp.description}</span>
                                   : <span style={{ fontSize: '12px', color: '#bbb' }}>Equipment</span>}
                          </td>
                          <td style={s.td}>
                            <span style={{ ...s.sBadge, ...resultStyle }}>
                              {c1.inspection_result === 'pass' ? '✅ Pass'
                                : c1.inspection_result === 'conditional_pass' ? '⚠️ Conditional'
                                : '❌ Fail'}
                            </span>
                          </td>
                          <td style={s.td}>
                            {c1.overhaul_extension_months
                              ? <span style={{ color: '#4caf50', fontWeight: 600 }}>+{c1.overhaul_extension_months} months</span>
                              : '—'}
                          </td>
                          <td style={s.td}>{fmt(c1.new_overhaul_due)}</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} style={{ padding: '16px 20px', background: '#f8f9ff', borderBottom: '2px solid #e0e0e0' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                                {c1.findings && <div><strong style={{ color: '#666' }}>Findings:</strong><br/>{c1.findings}</div>}
                                {c1.recommendations && <div><strong style={{ color: '#666' }}>Recommendations:</strong><br/>{c1.recommendations}</div>}
                                {c1.condition_notes && <div><strong style={{ color: '#666' }}>Condition:</strong><br/>{c1.condition_notes}</div>}
                                {c1.original_overhaul_due && <div><strong style={{ color: '#666' }}>Original Due:</strong> {fmt(c1.original_overhaul_due)} → <strong>{fmt(c1.new_overhaul_due)}</strong></div>}
                                {c1.next_inspection_due && <div><strong style={{ color: '#666' }}>Next C1 Due:</strong> {fmt(c1.next_inspection_due)}</div>}
                                {c1.inspector_organisation && <div><strong style={{ color: '#666' }}>Organisation:</strong> {c1.inspector_organisation}</div>}
                                {c1.work_order_number && <div><strong style={{ color: '#666' }}>Work Order:</strong> {c1.work_order_number}</div>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : !showC1Form && (
            <div style={s.empty}>No C1 inspections recorded. C1 pre-overhaul inspections (AS2290.1) can extend overhaul due dates when equipment passes condition assessment.</div>
          )}
        </Section>

        {/* Change-Outs Section */}
        <Section title={`🔄 Component Change-Outs (${changeouts.length})`}
          action={<button onClick={() => setShowChangeout(!showChangeout)} style={s.actionBtn}>{showChangeout ? 'Cancel' : '+ Record Change-Out'}</button>}>

          {showChangeout && (
            <form onSubmit={handleChangeout} style={s.formCard}>
              <h4 style={{ margin: '0 0 16px', fontSize: '14px', color: '#1a1a2e' }}>Record Ex Component Change-Out</h4>
              <div style={s.grid3}>
                <div style={s.field}>
                  <label style={s.fLabel}>Component Being Replaced *</label>
                  <select value={coComponentId} onChange={e => setCoComponentId(e.target.value)} style={s.fInput} required>
                    <option value="">Select component...</option>
                    {eq.components.filter(c => c.status === 'in_service').map(c => (
                      <option key={c.id} value={c.id}>#{c.schedule_number} {c.description} (S/N: {c.serial_number || 'N/A'})</option>
                    ))}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Change-Out Date *</label>
                  <input type="date" value={coDate} onChange={e => setCoDate(e.target.value)} style={s.fInput} required />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Reason *</label>
                  <select value={coReason} onChange={e => setCoReason(e.target.value)} style={s.fInput}>
                    <option value="overhaul_due">Overhaul Due</option>
                    <option value="fault">Fault</option>
                    <option value="damage">Damage</option>
                    <option value="upgrade">Upgrade</option>
                    <option value="mda_replacement">MDA Replacement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <h4 style={{ margin: '16px 0 12px', fontSize: '13px', color: '#666' }}>New Component Details</h4>
              <div style={s.grid4}>
                <div style={s.field}>
                  <label style={s.fLabel}>Serial Number</label>
                  <input value={coNewSerial} onChange={e => setCoNewSerial(e.target.value)} style={s.fInput} placeholder="New serial" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Certificate Number</label>
                  <input value={coNewCert} onChange={e => setCoNewCert(e.target.value)} style={s.fInput} placeholder="New cert" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Manufacturer</label>
                  <input value={coNewMfr} onChange={e => setCoNewMfr(e.target.value)} style={s.fInput} placeholder="Same or new" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Model</label>
                  <input value={coNewModel} onChange={e => setCoNewModel(e.target.value)} style={s.fInput} placeholder="Same or new" />
                </div>
              </div>

              <div style={s.grid3}>
                <div style={s.field}>
                  <label style={s.fLabel}>Work Order #</label>
                  <input value={coWorkOrder} onChange={e => setCoWorkOrder(e.target.value)} style={s.fInput} />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Performed By</label>
                  <input value={coPerformedBy} onChange={e => setCoPerformedBy(e.target.value)} style={s.fInput} placeholder="Name of person" />
                </div>
                <div style={s.field}>
                  <label style={s.fLabel}>Notes</label>
                  <input value={coNotes} onChange={e => setCoNotes(e.target.value)} style={s.fInput} placeholder="Additional details" />
                </div>
              </div>

              <button type="submit" disabled={!coComponentId || coSubmitting} style={{ ...s.submitBtn, marginTop: '12px' }}>
                {coSubmitting ? 'Recording...' : 'Record Change-Out'}
              </button>
            </form>
          )}

          {changeouts.length > 0 ? (
            <div style={s.tWrap}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Date</th><th style={s.th}>Component</th>
                  <th style={s.th}>Old S/N → New S/N</th><th style={s.th}>Reason</th>
                  <th style={s.th}>Work Order</th><th style={s.th}>Status</th><th style={s.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {changeouts.map(co => {
                    const st = CHANGEOUT_STATUS[co.status] || { label: co.status, color: '#999' };
                    return (
                      <tr key={co.id} style={s.tr}>
                        <td style={s.td}>{fmt(co.changeout_date)}</td>
                        <td style={{ ...s.td, fontWeight: 500 }}>#{co.schedule_number}</td>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>
                          {co.old_serial_number || '—'} → {co.new_serial_number || '—'}
                        </td>
                        <td style={s.td}>{REASON_LABELS[co.reason] || co.reason}</td>
                        <td style={s.td}>{co.work_order_number || '—'}</td>
                        <td style={s.td}><span style={{ ...s.sBadge, background: st.color }}>{st.label}</span></td>
                        <td style={s.td}>
                          {co.status === 'pending_eem' && (
                            <button onClick={() => handleEEMApprove(co.id)} style={s.smBtn}>EEM Approve</button>
                          )}
                          {co.status === 'eem_approved' && (
                            <button onClick={() => handlePSFUpdate(co.id)} style={{ ...s.smBtn, background: '#4caf50' }}>Mark PSF Updated</button>
                          )}
                          {co.status === 'complete' && (
                            <span style={{ fontSize: '12px', color: '#4caf50' }}>✓</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : !showChangeout && (
            <div style={s.empty}>No component change-outs recorded.</div>
          )}
        </Section>
      </div>

      {/* Footer */}
      <footer style={s.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};

// Helper components
const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <div style={{ marginBottom: '24px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', margin: 0 }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Info: React.FC<{ label: string; value?: string | null; alert?: boolean }> = ({ label, value, alert }) => (
  <div>
    <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase' as const, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '14px', color: alert ? '#d32f2f' : '#333', fontWeight: alert ? 600 : 400, marginTop: '2px' }}>{value || '—'}</div>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { marginTop: 'auto', padding: '16px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  backBtn: { padding: '6px 12px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  content: { padding: '24px 32px', maxWidth: '1500px' },
  card: { background: '#fff', borderRadius: '12px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '24px' },
  eqHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  eqTag: { margin: 0, fontSize: '28px', fontWeight: 700, color: '#1a1a2e' },
  eqDesc: { margin: '4px 0 0', fontSize: '16px', color: '#666' },
  badges: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' as const },
  badge: { padding: '4px 12px', borderRadius: '12px', color: '#fff', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' as const },
  exBadge: { padding: '4px 12px', borderRadius: '12px', background: '#fff3e0', color: '#e65100', fontSize: '12px', fontWeight: 600 },
  mdaBadge: { padding: '4px 12px', borderRadius: '12px', background: '#ffebee', color: '#c62828', fontSize: '12px', fontWeight: 600 },
  coBadge: { padding: '4px 12px', borderRadius: '12px', background: '#e3f2fd', color: '#1565c0', fontSize: '12px', fontWeight: 600 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  notes: { marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '8px', fontSize: '13px', color: '#555' },
  tWrap: { background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '10px 14px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const, borderBottom: '2px solid #e0e0e0', background: '#fafafa' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 14px', fontSize: '13px', color: '#333' },
  sBadge: { padding: '2px 8px', borderRadius: '10px', color: '#fff', fontSize: '10px', fontWeight: 600, textTransform: 'capitalize' as const, whiteSpace: 'nowrap' as const },
  protBadge: { padding: '2px 8px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0', fontSize: '11px', fontWeight: 600 },
  mdaFlag: { padding: '2px 6px', borderRadius: '4px', background: '#ffebee', color: '#c62828', fontSize: '10px', fontWeight: 600 },
  alertFlag: { padding: '2px 6px', borderRadius: '4px', background: '#fff3e0', color: '#e65100', fontSize: '10px', fontWeight: 600, marginLeft: '4px' },
  actionBtn: { padding: '8px 16px', background: '#0f3460', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  formCard: { background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column' as const, gap: '4px' },
  fLabel: { fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const },
  fInput: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' },
  submitBtn: { padding: '8px 20px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  smBtn: { padding: '4px 10px', background: '#0f3460', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 },
  dropZone: { border: '2px dashed #ddd', borderRadius: '10px', padding: '28px 20px', cursor: 'pointer', transition: 'all 0.2s' },
  docBadge: { padding: '2px 8px', borderRadius: '4px', background: '#f0f0f0', color: '#555', fontSize: '11px', fontWeight: 600 },
  empty: { padding: '32px', textAlign: 'center' as const, color: '#999', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  loadingFull: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontSize: '18px', color: '#999' },
  // Certificate link styles
  certLink: { color: '#1565c0', textDecoration: 'none', fontWeight: 600, fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', background: '#e3f2fd', borderRadius: '4px', cursor: 'pointer' },
  certNoDoc: { color: '#bbb', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  certNoDocBadge: { fontSize: '9px', background: '#f0f0f0', color: '#aaa', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 },
  // PSF Documents section styles
  docTypeHeader: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', marginBottom: '6px' },
  docTypeLabel: { fontSize: '14px', fontWeight: 600, color: '#1a1a2e' },
  docTypeCount: { padding: '1px 8px', background: '#e3f2fd', color: '#1565c0', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
  downloadBtn: { padding: '4px 12px', background: '#0f3460', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '11px', fontWeight: 600, display: 'inline-block' },
  componentTag: { padding: '2px 8px', background: '#fff3e0', color: '#e65100', borderRadius: '4px', fontSize: '11px', fontWeight: 500 },
  aiTag: { marginLeft: '6px', padding: '1px 5px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', fontSize: '10px', fontWeight: 600 },
  woTypeBadge: { padding: '2px 8px', borderRadius: '10px', color: '#fff', fontSize: '10px', fontWeight: 600, textTransform: 'capitalize' as const, whiteSpace: 'nowrap' as const },
};

export default EquipmentDetail;
